import type {
  OrderItem,
  CurrentOrderState,
  OrderProductSnapshot,
} from "./types";
import type {
  OrderData,
  ErrorData,
  IframeSource,
} from "@/types/iframe-messages";
import { createIframeMessage } from "@/types/iframe-messages";
import type { MenuProduct } from "@/types/menu-product";

/**
 * Итог корзины в копейках.
 *
 * Опции входят в цену ПОЗИЦИИ, а не отдельным слагаемым: сервер описывает
 * OrderItemGroup.price как «цена одной позиции группы — включая выбранные
 * опции», и клиентский расчёт обязан совпадать с ним, иначе итог поедет
 * после первого же ответа сервера.
 */
export const calculateCartTotal = (items: OrderItem[]): number =>
  items.reduce((total, item) => total + itemPrice(item) * item.quantity, 0);

/** Цена ОДНОЙ позиции: товар плюс выбранные опции. */
export const itemPrice = (item: OrderItem): number =>
  item.price + item.options.reduce((sum, o) => sum + o.price * o.quantity, 0);

/**
 * Sends iframe messages to parent window
 * @param type Message type
 * @param source Iframe source
 * @param payload Message payload
 */
export const sendIframeMessage = (
  type: "PREORDER_COMPLETED" | "PREORDER_ERROR" | "PREORDER_UPDATED",
  source: IframeSource,
  payload: OrderData | ErrorData
): void => {
  if (window.parent !== window) {
    const message = createIframeMessage(type, source, payload);
    console.log("Отправка сообщения iframe в родительское окно:", message);
    window.parent.postMessage(message, "*");
  }
};

/**
 * Получает уникальный ключ для вариантов товара на основе variantSettings.variantPropertyBinds
 * Создает строку из отсортированных пар propertyId-valueId
 * @param product - Товар для получения ключа вариантов
 * @returns Строка-ключ вариантов или пустая строка, если вариантов нет
 */
function getVariantKey(product: MenuProduct): string {
  const variantSettings = product.variantSettings;
  if (!variantSettings?.productVariantPropertyBinds) {
    return "";
  }

  const binds = variantSettings.productVariantPropertyBinds
    .map(
      (bind: {
        productVariantPropertyId: string;
        productVariantPropertyValueId: string;
      }) =>
        `${bind.productVariantPropertyId}:${bind.productVariantPropertyValueId || ""}`
    )
    .sort()
    .join("|");

  return binds;
}

/**
 * Получает уникальный ключ для вариантов товара из productSnapshot
 * Создает строку из отсортированных пар propertyId-valueId
 * @param snapshot - ProductSnapshot для получения ключа вариантов
 * @returns Строка-ключ вариантов или пустая строка, если вариантов нет
 */
function getVariantKeyFromSnapshot(snapshot: OrderProductSnapshot): string {
  if (snapshot.variantProperties.length === 0) {
    return "";
  }

  const binds = snapshot.variantProperties
    .map(
      (prop) => `${prop.variantPropertyId}:${prop.variantPropertyValueId || ""}`
    )
    .sort()
    .join("|");

  return binds;
}

/**
 * Сравнивает productSnapshot с Product с учетом вариантов и набора опций.
 * Товары считаются одинаковыми (сливаются в одну позицию корзины), если:
 * - У них одинаковый productId
 * - У них одинаковый набор опций (сравнение — забота вызывающей стороны:
 *   она знает отпечатки обеих позиций и передаёт готовый булев результат)
 * - У них одинаковые варианты (или оба без вариантов)
 * @param productSnapshot - ProductSnapshot из OrderItem
 * @param product - Product для сравнения
 * @param optionsEqual - совпадают ли наборы опций двух позиций
 * @returns true, если товары одинаковые (можно объединить в одну позицию)
 */
export function areProductsEqual(
  productSnapshot: OrderProductSnapshot,
  product: MenuProduct,
  optionsEqual: boolean = true
): boolean {
  // Сравниваем базовый идентификатор товара
  if (productSnapshot.productId !== product.id) {
    return false;
  }

  // Разный набор опций — разные позиции, даже у одного товара.
  if (!optionsEqual) {
    return false;
  }

  // Сравниваем варианты
  const variantKeySnapshot = getVariantKeyFromSnapshot(productSnapshot);
  const variantKeyProduct = getVariantKey(product);

  // Если варианты одинаковые (или оба отсутствуют) - товары одинаковые
  return variantKeySnapshot === variantKeyProduct;
}

// ===== УТИЛИТЫ ДЛЯ СИНХРОНИЗАЦИИ ЗАКАЗА =====

/**
 * Проверяет, нужно ли создавать новый заказ
 * @param currentOrder - Текущее состояние заказа
 * @returns true, если нужно создать заказ (нет id и есть товары)
 */
export function shouldCreateOrder(currentOrder: CurrentOrderState): boolean {
  return !currentOrder.id && currentOrder.items.length > 0;
}

/**
 * Проверяет, нужно ли обновлять существующий заказ
 * @param currentOrder - Текущее состояние заказа
 * @returns true, если нужно обновить заказ (есть id)
 */
export function shouldUpdateOrder(currentOrder: CurrentOrderState): boolean {
  return !!currentOrder.id;
}
